import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X as XIcon, ArrowUpRight, Loader2 } from "lucide-react";
import { fetchSearchIndex, flattenIndex, scoreRows } from "@/lib/search_index";

/**
 * GlobalSearchModal — Cmd-K-style site-wide search.
 *
 * v60.99.47 — opened from the homepage navbar (icon button + ⌘K
 * keyboard shortcut). Renders a centered modal with an input + result
 * list grouped by category. Hitting Enter on a highlighted result
 * navigates to that page (same-tab); clicking a result does the same.
 *
 * Uses the shared /lib/search_index module so the scoring logic lives
 * in exactly one place (the /sitemap page filter uses the same module).
 *
 * State-flow notes (react-hooks/set-state-in-effect compliant):
 *   • fetchSearchIndex is awaited inside a useEffect guarded by a
 *     `cancelled` flag so we never call setState after unmount.
 *   • The active result index is `clampedActiveIdx` — derived during
 *     render rather than via a reset-on-change useEffect. This avoids
 *     the React 19 anti-pattern of cascading state updates.
 *   • The query intentionally persists across modal open/close — a
 *     UX win (re-open returns the user to their last search) AND
 *     removes the need for a reset-on-close useEffect.
 */
export default function GlobalSearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  // loading starts true and resolves to false exactly once (when the
  // sitemap-index JSON arrives). The fetch happens on mount of the
  // navbar — NOT on first modal-open — because the index is ~3 KB and
  // caching it eagerly removes the noticeable lag on the first
  // ⌘K-press. Same pattern as /sitemap (Sitemap.jsx).
  const [loading, setLoading] = useState(true);
  // activeIdx is "the user's intent" — we clamp it during render
  // against the actual result-list length so the index is always
  // valid without requiring a reset useEffect.
  const [activeIdx, setActiveIdx] = useState(0);

  // Fetch the search index on mount. Cached at module-level in
  // search_index.js so the Sitemap page + this modal share the
  // same network request.
  useEffect(() => {
    let cancelled = false;
    fetchSearchIndex()
      .then((data) => { if (!cancelled) setRows(flattenIndex(data)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Esc closes the modal — always-on keyboard listener while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Compute filtered results — top 30 ranked by score. When the query
  // is empty we show the first 12 rows as a "browse" surface so the
  // modal is useful even before the user types anything.
  const results = useMemo(() => {
    if (!query.trim()) return rows.slice(0, 12);
    return scoreRows(rows, query).slice(0, 30);
  }, [rows, query]);

  // Group results by category for the rendered list.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of results) {
      if (!map.has(r.group)) map.set(r.group, { id: r.group, label: r.groupLabel, items: [] });
      map.get(r.group).items.push(r);
    }
    return Array.from(map.values());
  }, [results]);

  // Flat index of (group, row, globalIdx) for keyboard navigation.
  const flatNav = useMemo(() => {
    const f = [];
    let i = 0;
    for (const g of grouped) for (const r of g.items) f.push({ ...r, _i: i++ });
    return f;
  }, [grouped]);

  // Derived (not stored) clamped active index — auto-resets to 0 when
  // results shrink, without needing a reset-on-change useEffect.
  const clampedActiveIdx = flatNav.length === 0 ? 0 : Math.min(activeIdx, flatNav.length - 1);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(Math.min(clampedActiveIdx + 1, Math.max(0, flatNav.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(Math.max(clampedActiveIdx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flatNav[clampedActiveIdx];
      if (target) {
        onClose?.();
        navigate(target.path);
      }
    }
  };

  // Reset activeIdx whenever the user retypes — done inline in the
  // onChange handler instead of via a useEffect on `query` change.
  const onQueryChange = (e) => {
    setQuery(e.target.value);
    setActiveIdx(0);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-16 sm:pt-24 px-4"
      onClick={onClose}
      data-testid="global-search-modal"
    >
      <div
        className="w-full max-w-2xl bg-white border border-zinc-300 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="relative border-b border-zinc-200">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600 pointer-events-none" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={onQueryChange}
            onKeyDown={onKeyDown}
            placeholder="Search widgets, SEO tools, blog posts…"
            className="w-full bg-white text-zinc-900 placeholder:text-zinc-500 px-12 py-4 text-base outline-none"
            data-testid="global-search-input"
            aria-label="Search the FeedPulse site"
            // autoFocus is safe here because the modal only mounts
            // when `open` is true (see early-return above).
            autoFocus
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-zinc-700 hover:text-zinc-900"
            aria-label="Close search"
            data-testid="global-search-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="global-search-results">
          {loading && rows.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-zinc-700 px-4 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading index…
            </div>
          ) : flatNav.length === 0 ? (
            <div className="text-sm text-zinc-700 px-4 py-6" data-testid="global-search-empty">
              No matches for <span className="font-bold text-zinc-900">&ldquo;{query.trim()}&rdquo;</span> — try a broader keyword.
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.id} className="py-1">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-700 font-bold px-4 py-1.5">
                  / {g.label}
                </div>
                {g.items.map((r) => {
                  const isActive = r._i === clampedActiveIdx;
                  return (
                    <button
                      key={r.path}
                      type="button"
                      onMouseEnter={() => setActiveIdx(r._i)}
                      onClick={() => { onClose?.(); navigate(r.path); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? "bg-klein/10 text-klein" : "text-zinc-900 hover:bg-zinc-50"}`}
                      data-testid={`global-search-result-${r.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                    >
                      <span className="font-semibold text-sm truncate flex-1">{r.label}</span>
                      <span className={`font-mono text-[11px] truncate ${isActive ? "text-klein" : "text-zinc-700"}`}>{r.path}</span>
                      <ArrowUpRight className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? "opacity-100" : "opacity-0"}`} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-zinc-200 px-4 py-2.5 text-[11px] text-zinc-700 flex items-center gap-4">
          <span><kbd className="px-1.5 py-0.5 border border-zinc-300 bg-zinc-50 text-[10px] font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="px-1.5 py-0.5 border border-zinc-300 bg-zinc-50 text-[10px] font-mono">Enter</kbd> open</span>
          <span><kbd className="px-1.5 py-0.5 border border-zinc-300 bg-zinc-50 text-[10px] font-mono">Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
